import React, { useState } from "react";
import BarChartIcon from "@mui/icons-material/BarChart";
import TableChartIcon from "@mui/icons-material/TableChart";
import "./ImpactResults.css";
import { TABS } from "../../../conts/navigationConstants";
import { UNITS } from "../../../conts/simulationConstants";

const fmt = (n, d = 0) =>
  Number(n ?? 0).toLocaleString("fr-FR", { maximumFractionDigits: d });
const pct = (part, total) => (total > 0 ? (part / total) * 100 : 0);
const clamp = (n) => Math.min(Math.max(n, 0), 100);

/* The three areas. `key` is the field name used in the rows. */
const FOCUS = [
  { id: "menace", label: "Menacé", key: "menace" },
  { id: "saved", label: "Préservé", key: "preserve" },
  { id: "burned", label: "Sinistré", key: "sinistre" },
];

/* A bar is just a track + a fill; `width` is a percentage (0-100). */
const Bar = ({ width, focus }) => (
  <div className="ir-bar">
    <div
      className={`ir-bar__fill ir-bar__fill--${focus}`}
      style={{ width: `${clamp(width)}%` }}
    />
  </div>
);

/* Chart rows for the selected area.
   Every bar is measured against the TOTAL of that same area:
   - Menacé    -> each class = its share of the total menacé
   - Préservé  -> each class = its share of the total préservé
   - Sinistré  -> each class = its share of the total sinistré
   The "Total" row is the reference, so its bar is full. */
const ChartRows = ({ rows, unit, decimals, focus }) => {
  const { key, label } = FOCUS.find((f) => f.id === focus);
  const base = rows.find((r) => r.strong)?.[key] ?? 0;

  return rows.map((row) => {
    const value = row[key];
    const share = row.strong ? (base > 0 ? 100 : 0) : pct(value, base);

    return (
      <div
        key={row.key}
        className={`ir-row ${row.strong ? "ir-row--strong" : ""}`}
      >
        <span className="ir-row__label">{row.label}</span>
        <Bar width={share} focus={focus} />
        <b className="ir-row__value">
          {fmt(value, decimals)} {unit}
          {!row.strong && (
            <small>
              {fmt(share, 1)} % du {label.toLowerCase()}
            </small>
          )}
        </b>
      </div>
    );
  });
};

/* Buildings tab: the three bars stacked, always visible.
   Here the bars are compared to the menacé count, since the goal is to
   compare the three areas with each other. */
const BuildingRows = ({ buildings }) => {
  const total = buildings.menace.count;
  const row = {
    menace: total,
    preserve: buildings.preserve.count,
    sinistre: buildings.sinistre.count,
  };

  return FOCUS.map((f) => {
    const value = row[f.key];
    const width = f.id === "menace" ? 100 : pct(value, total);

    return (
      <div key={f.id} className="ir-row">
        <span className="ir-row__label ir-row__label--dot">
          <i className={`ir-dot ir-dot--${f.id}`} />
          {f.label}
        </span>
        <Bar width={width} focus={f.id} />
        <b className="ir-row__value">
          {fmt(value)}
          {f.id !== "menace" && (
            <small>{fmt(width, 1)} % du menacé</small>
          )}
        </b>
      </div>
    );
  });
};

const TableRows = ({ rows, unit, decimals }) => (
  <div className="ir-table-wrap">
    <table className="ir-table">
      <thead>
        <tr>
          <th>Classe ({unit})</th>
          <th>Menacé</th>
          <th>Préservé</th>
          <th>Sinistré</th>
          <th>% sauvé</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} className={r.strong ? "is-total" : ""}>
            <td>{r.label}</td>
            <td>{fmt(r.menace, decimals)}</td>
            <td>{fmt(r.preserve, decimals)}</td>
            <td>{fmt(r.sinistre, decimals)}</td>
            <td>{fmt(pct(r.preserve, r.menace), 1)} %</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ImpactResults = ({ results, warnings = [] }) => {
  const [tab, setTab] = useState("area");
  const [view, setView] = useState("table"); // "chart" | "table"
  const [focus, setFocus] = useState("menace"); // "menace" | "saved" | "burned"

  if (!results) return null;

  const { zones, buildings, forest_types: classes = [] } = results;
  const { menace, preserve, sinistre } = zones;

  /* ---- stat boxes ---- */
  const areaPct = pct(preserve.area_ha, menace.area_ha);
  const bldPct = pct(buildings.preserve.count, buildings.menace.count);
  const co2Pct = pct(preserve.co2_t, menace.co2_t);

  /* ---- rows per tab (hide classes with nothing in the menacé zone) ---- */
  const visible = classes.filter((c) => c.area_ha.menace > 0);

  const rowsByTab = {
    area: [
      {
        key: "total",
        label: "Total",
        strong: true,
        menace: menace.area_ha,
        preserve: preserve.area_ha,
        sinistre: sinistre.area_ha,
      },
      ...visible.map((c) => ({
        key: c.class,
        label: c.label,
        menace: c.area_ha.menace,
        preserve: c.area_ha.preserve,
        sinistre: c.area_ha.sinistre,
      })),
      ...(menace.other_ha > 0
        ? [
            {
              key: "other",
              label: "Non classé",
              menace: menace.other_ha,
              preserve: preserve.other_ha,
              sinistre: sinistre.other_ha,
            },
          ]
        : []),
    ],
    co2: [
      {
        key: "total",
        label: "Total",
        strong: true,
        menace: menace.co2_t,
        preserve: preserve.co2_t,
        sinistre: sinistre.co2_t,
      },
      ...visible.map((c) => ({
        key: c.class,
        label: c.label,
        menace: c.co2.menace.co2_t,
        preserve: c.co2.preserve.co2_t,
        sinistre: c.co2.sinistre.co2_t,
      })),
    ],
  };

  const isBuildings = tab === "buildings";
  const { unit, decimals } = UNITS[tab] ?? {};
  const rows = rowsByTab[tab] ?? [];
  const showTable = !isBuildings && view === "table";

  return (
    <div className="ir">
      {/* {warnings.length > 0 && (
        <ul className="ir-warnings">
          {warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )} */}

      <div className="ir-stats">
        <div className="ir-stat">
          <small>Surface préservée</small>
          <strong>{fmt(preserve.area_ha, 1)} ha</strong>
          <span>{fmt(areaPct, 1)} % de la zone menacée</span>
        </div>
        <div className="ir-stat">
          <small>Bâtiments sauvés</small>
          <strong>{fmt(buildings.preserve.count)}</strong>
          <span>
            {fmt(bldPct, 1)} % sur {fmt(buildings.menace.count)}
          </span>
        </div>
        <div className="ir-stat">
          <small>CO₂ évité</small>
          <strong>{fmt(preserve.co2_t)} t</strong>
          <span>{fmt(co2Pct, 1)} % du CO₂ menacé</span>
        </div>
      </div>

      <div className="ir-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`ir-tab ${tab === t.id ? "is-active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <section className="ir-card">
        {/* legend (area filter) + chart/table toggle: not needed on the buildings tab */}
        {!isBuildings && (
          <div className="ir-card__top">
            {!showTable ? (
              <div
                className="ir-legend"
                role="radiogroup"
                aria-label="Filtrer par zone"
              >
                {FOCUS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    role="radio"
                    aria-checked={focus === f.id}
                    className={`ir-legend__item ${focus === f.id ? "is-active" : ""}`}
                    onClick={() => setFocus(f.id)}
                  >
                    <i className={`ir-dot ir-dot--${f.id}`} />
                    {f.label}
                  </button>
                ))}
              </div>
            ) : (
              <span />
            )}

            <div
              className="ir-toggle"
              role="group"
              aria-label="Mode d’affichage"
            >
              <button
                type="button"
                className={view === "chart" ? "is-active" : ""}
                aria-pressed={view === "chart"}
                aria-label="Afficher le graphique"
                title="Graphique"
                onClick={() => setView("chart")}
              >
                <BarChartIcon fontSize="small" />
              </button>
              <button
                type="button"
                className={view === "table" ? "is-active" : ""}
                aria-pressed={view === "table"}
                aria-label="Afficher le tableau"
                title="Tableau"
                onClick={() => setView("table")}
              >
                <TableChartIcon fontSize="small" />
              </button>
            </div>
          </div>
        )}

        {isBuildings ? (
          <BuildingRows buildings={buildings} />
        ) : showTable ? (
          <TableRows rows={rows} unit={unit} decimals={decimals} />
        ) : (
          <ChartRows
            rows={rows}
            unit={unit}
            decimals={decimals}
            focus={focus}
          />
        )}
      </section>
    </div>
  );
};

export default ImpactResults;